import { useEffect, useState } from 'react';
import type { IHighlight } from 'react-pdf-highlighter';
import {
  buildExperienceAssets,
  type ExperienceAssets,
  type StoryboardHighlights,
} from '@libertify.frontend/adapters';
import { fetchJsonAsset } from '../utils';
import type { Cuepoint } from '../types';

export type ExperienceAssetsResult = ExperienceAssets<IHighlight, Cuepoint>;

const EMPTY: ExperienceAssetsResult = {
  sections: [],
  cuepoints: [],
  highlights: [],
};

/**
 * Client-side loader for the publication's highlight asset, returning the
 * chapters / cuepoints / highlights triple. The sales app does this
 * server-side in `getExperienceData`; admin (a Vite SPA with the
 * publication already in hand, but only the asset URL — not the JSON)
 * uses this hook instead. Both share the `buildExperienceAssets` parser
 * so the shapes can't drift.
 */
export const useExperienceAssets = (
  highlightAssetUrl?: string | null,
): ExperienceAssetsResult => {
  const [assets, setAssets] = useState<ExperienceAssetsResult>(EMPTY);

  useEffect(() => {
    if (!highlightAssetUrl) return;
    let cancelled = false;

    fetchJsonAsset<StoryboardHighlights>(highlightAssetUrl)
      .then((asset) => {
        if (cancelled) return;
        setAssets(buildExperienceAssets<IHighlight, Cuepoint>(asset));
      })
      .catch((err) => {
        // Keep empty assets (no chapters/sync) rather than leaving the
        // promise to reject unhandled.
        if (!cancelled) console.warn('[experience] asset parse failed', err);
      });

    return () => {
      cancelled = true;
    };
  }, [highlightAssetUrl]);

  return assets;
};
